import { useEffect, useState } from 'react'
import { useLocation, useParams } from 'react-router-dom'
import type { RepositoryState, Vehicle, VehicleRepository } from '../data/vehicles'
import { DemoVehicleRepository } from '../data/vehicles'
import { getApprovedHeroImage } from '../data/media'
import { getLocalizedPath } from '../i18n/routes'
import { Button } from '../ui/Button'
import { Link } from '../ui/Link'
import { CommercialLink } from '../ui/CommercialLink'
import { VerificationMark } from '../ui/VerificationMark'
import { VehicleAdditionalContent, VehicleEquipment, VehicleSpecifications } from '../components/inventory/VehicleSpecifications'
import { useVehicleFactLabels } from '../components/inventory/VehicleFacts'
import { VehicleFacts } from '../components/inventory/VehicleFacts'
import { VehicleGallery } from '../components/inventory/VehicleGallery'
import { vehicleLabel } from '../components/inventory/VehicleImage'
import { useInventoryTranslation } from '../components/inventory/useInventoryTranslation'
import { useRouteMetadata } from '../components/inventory/useRouteMetadata'
import { PageHero, type PageHeroMedia } from '../components/page-hero/PageHero'
import '../components/inventory/inventory.css'

const defaultRepository = new DemoVehicleRepository()

export function VehicleDetailPage({ repository = defaultRepository }: { repository?: VehicleRepository }) {
  const { language, t } = useInventoryTranslation()
  const factLabels = useVehicleFactLabels()
  const { pathname } = useLocation()
  const { slug = '' } = useParams<{ slug: string }>()
  const [state, setState] = useState<RepositoryState<Vehicle>>({ status: 'loading' })
  const [request, setRequest] = useState(0)

  useEffect(() => {
    let active = true
    setState({ status: 'loading' })
    repository.getBySlug(slug).then((result) => {
      if (active) setState(result)
    }).catch((error: unknown) => {
      if (active) setState({ status: 'error', error: error instanceof Error ? error : new Error(String(error)) })
    })
    return () => {
      active = false
    }
  }, [repository, request, slug])

  useEffect(() => {
    if (state.status === 'loading') return
    const frame = window.requestAnimationFrame(() => {
      document.querySelector<HTMLElement>('main h1')?.focus({ preventScroll: true })
    })
    return () => window.cancelAnimationFrame(frame)
  }, [state.status])

  const vehicle = state.status === 'ready' ? state.data : null
  const label = vehicle ? vehicleLabel(vehicle) : ''
  useRouteMetadata({
    title: vehicle ? t('detailTitle', { vehicle: label }) : t('detailLoadingTitle'),
    description: vehicle ? t('detailDescription', { vehicle: label }) : t('detailLoadingDescription'),
    pathname,
    image: vehicle?.images[0]?.src,
    language,
  })

  if (state.status === 'loading') {
    return (
      <div className="vehicle-detail-state page-frame" role="status">
        <span className="inventory-state__line" aria-hidden="true" />
        <p>{t('loading')}</p>
      </div>
    )
  }

  if (state.status === 'error') {
    return (
      <div className="vehicle-detail-state page-frame" role="alert">
        <h1 tabIndex={-1}>{t('errorTitle')}</h1>
        <p>{t('errorBody')}</p>
        <Button type="button" variant="secondary" onClick={() => setRequest((value) => value + 1)}>{t('retry')}</Button>
      </div>
    )
  }

  if (state.status === 'empty') {
    return (
      <div className="vehicle-detail-state page-frame">
        <h1 tabIndex={-1}>{t('detailUnavailable')}</h1>
        <Link variant="secondary" to={getLocalizedPath('vehicles', language)}>{t('backToInventory')}</Link>
      </div>
    )
  }

  const contactParams = new URLSearchParams({ vehicle: `${state.data.id} ${vehicleLabel(state.data)}` })
  const contactPath = `${getLocalizedPath('contacts', language)}?${contactParams.toString()}`
  const heroImage = getApprovedHeroImage(state.data.images, 'vehicle-detail')
  const heroMedia: PageHeroMedia | undefined = heroImage
    ? { src: heroImage.src, alt: t('imageFrontThreeQuarter', { vehicle: vehicleLabel(state.data) }), focalPoint: heroImage.focalPoint }
    : undefined

  return (
    <article className="vehicle-detail">
      <PageHero variant="vehicle" eyebrow={t('detailEyebrow')} title={state.data.model} media={heroMedia}>
        <Link variant="text" to={getLocalizedPath('vehicles', language)}>{t('backToInventory')}</Link>
        <p className="vehicle-detail__make">{state.data.make}</p>
        <p className="vehicle-detail__reference">{t('reference', { reference: state.data.id })}</p>
      </PageHero>

      <div className="page-frame vehicle-overview">
        <div>
          <p className="vehicle-overview__eyebrow">{t(state.data.offerType === 'rental' ? 'rentalOffer' : 'saleOffer')}</p>
          <p className="vehicle-overview__price">{state.data.facts.price ? factLabels.money(state.data.facts.price.amountEuro) : t('priceOnRequest')}</p>
          {state.data.commercialNotes ? <p>{state.data.commercialNotes[language]}</p> : null}
          {state.data.availability ? <span className="vehicle-overview__status">{t(({ available: 'availabilityAvailable', reserved: 'availabilityReserved', sold: 'availabilitySold' } as const)[state.data.availability])}</span> : null}
        </div>
        <div className="vehicle-overview__action">
          <Link variant="primary" to={contactPath}>{t('contactAction')}</Link>
          {state.data.source === 'audit-demo' && state.data.auditedAt ? <p>{t('snapshotNote', { date: new Intl.DateTimeFormat(language, { dateStyle: 'medium' }).format(new Date(`${state.data.auditedAt}T12:00:00`)) })}</p> : null}
        </div>
      </div>
      <div className="page-frame vehicle-detail__gallery-wrap">
        <VehicleGallery vehicle={state.data} />
      </div>

      <section className="vehicle-detail__facts" aria-labelledby="vehicle-facts-title">
        <div className="page-frame vehicle-detail__section-grid">
          <div>
            <h2 id="vehicle-facts-title">{t('technicalDataTitle')}</h2>
            {!state.data.facts.year && !state.data.facts.mileageKm ? <p>{t('dataIncomplete')}</p> : null}
          </div>
          <VehicleFacts vehicle={state.data} />
        </div>
      </section>

      <VehicleSpecifications vehicle={state.data} />
      <VehicleAdditionalContent vehicle={state.data} />
      <VehicleEquipment vehicle={state.data} />

      <section className="vehicle-detail__assurance" aria-labelledby="vehicle-guarantee-title">
        <div className="page-frame vehicle-detail__section-grid">
          <div className="vehicle-detail__guarantee">
            <VerificationMark label={t('guaranteeMark')} />
            <h2 id="vehicle-guarantee-title">{t('guaranteeTitle')}</h2>
            <p>{t('guaranteeBody', { reference: state.data.id })}</p>
          </div>

        </div>
      </section>

      <section className="vehicle-detail__contact commercial-cta" aria-label={t('contactAction')}>
        <div className="page-frame vehicle-detail__contact-grid">
          <div>
            <p className="vehicle-detail__make">{state.data.make}</p>
            <h2>{state.data.model}</h2>
          </div>
          <CommercialLink
            to={contactPath}
            aria-label={t('contactActionLabel', { vehicle: vehicleLabel(state.data), reference: state.data.id })}
          >
            {t('contactAction')}
          </CommercialLink>
        </div>
      </section>
    </article>
  )
}
